import { useEffect, useCallback } from 'react';
import {
  Breadcrumbs,
  Link,
  Page,
  PageBody,
  PageBreadcrumbs,
  PageHeader,
  PageHeaderTitle,
  PageHeaderDescription,
  PageHeaderActions,
  Button,
  Banner,
  Stack,
  Spinner,
  Text,
  Box,
} from '@customerio/pluma-components/react';
import { useAuthQuery } from '@/services/auth';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { useSearchParams } from 'react-router-dom';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { AuditLogsTable } from './AuditLogsTable';
import type { AuditLogResponse } from '../types';
import { AppFooter } from '@/components/app-footer';
import { FeedbackModal, useFeedbackModal } from '@/components/feedback-modal';
import { useAuditLogsData } from '../hooks/useAuditLogsData';

interface AuditLogsPageContainerProps {
  context: 'account' | 'workspace';
  contextId: number | null | undefined;
  breadcrumbs: Array<{ text: string; href?: string }>;
  pageTitle: string;
  pageDescription: string;
  backUrlBase: string;
  feedbackFeature: string;
}

export const AuditLogsPageContainer: React.FC<AuditLogsPageContainerProps> = ({
  context,
  contextId,
  breadcrumbs,
  pageTitle,
  pageDescription,
  backUrlBase,
  feedbackFeature,
}) => {
  const auth = useAuthQuery();
  const { track } = useAnalytics();
  const [searchParams] = useSearchParams();
  const navigate = useSafeNavigate();
  const feedbackModal = useFeedbackModal();

  const currentAccountId = auth.data?.account?.id;

  const {
    filters,
    auditLogId,
    accumulatedLogs,
    auditLogsQuery,
    singleLogQuery,
    allUsersQuery,
    handleFiltersChange,
    handleLoadUsers,
    isLoadingUsers,
  } = useAuditLogsData({
    context,
    contextId,
    isAuthLoading: auth.isLoading,
    accountId: currentAccountId,
  });

  // Track page view - useCallback to prevent re-creating on every render
  // NOTE: We compute hasFilters inside the callback using the current filters value
  // to avoid adding filters to dependencies which would cause infinite re-render loops
  const trackPageView = useCallback(() => {
    // Check if any filters are applied (excluding pagination/sorting)
    const hasFilters = !!(
      filters.user_id != null ||
      (filters.ip_addr != null && filters.ip_addr !== '') ||
      (filters.object_type != null && filters.object_type !== '') ||
      (filters.object_id != null && filters.object_id !== '') ||
      (filters.attribute_name != null && filters.attribute_name !== '') ||
      (filters.action != null && filters.action !== '') ||
      (filters.start_time != null && filters.start_time !== '') ||
      (filters.end_time != null && filters.end_time !== '')
    );

    track('auditLogs', 'viewed', {
      contextType: context,
      workspaceId: contextId,
      hasFilters,
    });
  }, [context, contextId, track]);

  usePageViewTracking(trackPageView, contextId !== null);

  // Eagerly load users on mount to avoid popover closing issue
  useEffect(() => {
    handleLoadUsers();
  }, [handleLoadUsers]);

  const AuditLogsPageLayout = ({
    children,
    customBreadcrumbs,
  }: {
    children: React.ReactNode;
    customBreadcrumbs?: Array<{ text: string; href?: string }>;
  }) => (
    <Page style={{ width: '100%' }}>
      <PageBreadcrumbs>
        <Breadcrumbs breadcrumbs={customBreadcrumbs ?? breadcrumbs} />
      </PageBreadcrumbs>
      {children}
      <AppFooter />
    </Page>
  );

  // Show loading state while auth is loading
  if (auth.isLoading) {
    return (
      <AuditLogsPageLayout>
        <PageHeader>
          <PageHeaderTitle>{pageTitle}</PageHeaderTitle>
          <PageHeaderDescription>{pageDescription}</PageHeaderDescription>
        </PageHeader>
        <PageBody>
          <Box p="800" textAlign="center">
            <Spinner />
            <Text mt="300">Loading...</Text>
          </Box>
        </PageBody>
      </AuditLogsPageLayout>
    );
  }

  // Check if auth data is available
  if (auth.data?.account == null) {
    const errorMessage =
      auth.error != null
        ? `Unable to load account information: ${
            auth.error instanceof Error ? auth.error.message : 'Unknown error'
          }. Please try refreshing the page.`
        : 'Unable to load account information. Please try refreshing the page.';

    return (
      <AuditLogsPageLayout>
        <PageHeader>
          <PageHeaderTitle>{pageTitle}</PageHeaderTitle>
          <PageHeaderDescription>{pageDescription}</PageHeaderDescription>
        </PageHeader>
        <PageBody>
          <Banner variant="error" title="Authentication Error">
            <Text>{errorMessage}</Text>
          </Banner>
        </PageBody>
      </AuditLogsPageLayout>
    );
  }

  // Handle deep link to specific audit log
  if (auditLogId != null) {
    if (singleLogQuery.isLoading) {
      return (
        <AuditLogsPageLayout>
          <PageHeader>
            <PageHeaderTitle>{pageTitle}</PageHeaderTitle>
            <PageHeaderDescription>{pageDescription}</PageHeaderDescription>
          </PageHeader>
          <PageBody>
            <Box p="800" textAlign="center">
              <Spinner />
              <Text mt="300">Loading audit log...</Text>
            </Box>
          </PageBody>
        </AuditLogsPageLayout>
      );
    }

    if (singleLogQuery.error != null || singleLogQuery.data == null) {
      const singleLogBreadcrumbs = [...breadcrumbs, { text: `Audit Log #${auditLogId}` }];
      const errorMessage =
        singleLogQuery.error != null
          ? `Error loading audit log: ${
              singleLogQuery.error instanceof Error ? singleLogQuery.error.message : 'Unknown error'
            }`
          : `Audit log #${auditLogId} not found`;
      const errorDetails =
        'This audit log may not exist, may have been deleted, or you may not have permission to view it.';

      return (
        <AuditLogsPageLayout customBreadcrumbs={singleLogBreadcrumbs}>
          <PageHeader>
            <PageHeaderTitle>{pageTitle}</PageHeaderTitle>
            <PageHeaderDescription>{pageDescription}</PageHeaderDescription>
          </PageHeader>
          <PageBody>
            <Banner variant="error" title={errorMessage}>
              <Text>{errorDetails}</Text>
            </Banner>
          </PageBody>
        </AuditLogsPageLayout>
      );
    }

    // Create a response object compatible with AuditLogsTable
    const deepLinkedResponse: AuditLogResponse = {
      data: [singleLogQuery.data.body.data],
      users: singleLogQuery.data.body.user != null ? [singleLogQuery.data.body.user] : [],
      pagination: {
        limit: 1,
        sort_order: 'desc',
        next_cursor: 0,
      },
    };

    const singleLogBreadcrumbs = [...breadcrumbs, { text: `Audit Log #${auditLogId}` }];

    return (
      <AuditLogsPageLayout customBreadcrumbs={singleLogBreadcrumbs}>
        <PageHeader>
          <PageHeaderTitle>Audit Log #{auditLogId}</PageHeaderTitle>
          <PageHeaderDescription>Viewing a single audit log entry.</PageHeaderDescription>
        </PageHeader>
        <PageBody>
          <Stack gap="400">
            <Box>
              <Link
                icon="chevron-left"
                variant="secondary"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(backUrlBase);
                }}
              >
                Back to All Logs
              </Link>
            </Box>
            <AuditLogsTable
              data={deepLinkedResponse}
              isLoading={false}
              error={undefined}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              highlightedLogId={auditLogId.toString()}
              context={context}
              contextId={contextId ?? 0}
              allUsers={allUsersQuery.data?.body.users ?? []}
              isLoadingUsers={isLoadingUsers}
              onLoadUsers={handleLoadUsers}
            />
          </Stack>
        </PageBody>
      </AuditLogsPageLayout>
    );
  }

  // Normal audit logs list view
  return (
    <AuditLogsPageLayout>
      <PageHeader>
        <PageHeaderTitle>{pageTitle}</PageHeaderTitle>
        <PageHeaderActions>
          <Button variant="secondary" onClick={() => feedbackModal.show()}>
            Send us feedback
          </Button>
        </PageHeaderActions>
        <PageHeaderDescription>{pageDescription}</PageHeaderDescription>
      </PageHeader>
      <FeedbackModal feature={feedbackFeature} eventCategory="auditLogs" />
      <PageBody>
        {/* Audit Logs Table */}
        <AuditLogsTable
          data={accumulatedLogs}
          isLoading={auditLogsQuery?.isLoading && filters.cursor == null}
          error={auditLogsQuery?.error}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          highlightedLogId={searchParams.get('audit_log_id') ?? undefined}
          context={context}
          contextId={contextId ?? 0}
          allUsers={allUsersQuery.data?.body.users ?? []}
          isLoadingUsers={isLoadingUsers}
          onLoadUsers={handleLoadUsers}
        />
      </PageBody>
    </AuditLogsPageLayout>
  );
};
